import {
  faArrowDownAZ,
  faArrowDownShortWide,
  faArrowUpAZ,
  faArrowUpShortWide,
} from "@fortawesome/free-solid-svg-icons";
import type { SortingOptions } from "components/UI/SortByPicker";
import { TFunction } from "i18next";

/** How the tracks and waypoints lists can be sorted ("createdDate" is the order the server returns them in) */
export type ItemSortBy = "createdDate" | "name" | "length";
export type TracksSortByOptions = ItemSortBy;
/** Waypoints have no length */
export type WaypointsSortByOptions = Exclude<ItemSortBy, "length">;

/** The choices of the sort picker for the given ways of sorting */
export const getSortingOptions = <SortBy extends ItemSortBy>(
  t: TFunction,
  sortBys: readonly SortBy[]
): SortingOptions<SortBy>[] => {
  const options: SortingOptions<ItemSortBy>[] = [
    {
      value: "name",
      label: t("sorting.name.AZ"),
      icon: faArrowDownAZ,
      direction: "asc",
    },
    {
      value: "name",
      label: t("sorting.name.ZA"),
      icon: faArrowUpAZ,
      direction: "desc",
    },
    {
      value: "createdDate",
      label: t("sorting.date.from_latest"),
      icon: faArrowDownShortWide,
      direction: "desc",
    },
    {
      value: "createdDate",
      label: t("sorting.date.from_oldest"),
      icon: faArrowUpShortWide,
      direction: "asc",
    },
    {
      value: "length",
      label: t("sorting.length.from_shortest"),
      icon: faArrowDownShortWide,
      direction: "asc",
    },
    {
      value: "length",
      label: t("sorting.length.from_longest"),
      icon: faArrowUpShortWide,
      direction: "desc",
    },
  ];
  const isPicked = (
    option: SortingOptions<ItemSortBy>
  ): option is SortingOptions<SortBy> =>
    (sortBys as readonly ItemSortBy[]).includes(option.value);
  return options.filter(isPicked);
};

/** A sorted copy of the items (the items are in the order the server returned them, oldest first) */
export const sortItems = <Item extends { name: string; length?: number }>(
  items: Item[] | undefined,
  sortBy: ItemSortBy,
  ascending: boolean
): Item[] | undefined => {
  if (!items) return undefined;
  const sorted =
    sortBy === "name"
      ? [...items].sort((a, b) => a.name.localeCompare(b.name))
      : sortBy === "length"
        ? [...items].sort((a, b) => (a.length ?? 0) - (b.length ?? 0))
        : [...items];
  return ascending ? sorted : sorted.reverse();
};
